import type { AvailabilitiesProps } from '@ab-core/availabilities';
import { getClassNames } from '@ab-core/functions/styles/classNameFormatter';
import type { PositionPriceObject } from '@ab-core/graphql/dist';
import type { PricesType } from '@ab-core/prices';
import type { ComparableProductType } from '@ab-core/product-comparison';
import type { FC, ReactNode } from 'react';
import React from 'react';
import type {
    AdditionalLinksContainerProps,
    IconsContainerProps,
    IdsContainerProps,
    ImageContainerProps,
    InformationContainerType,
    NameContainerProps,
    OrderContainerProps,
    TagsContainerProps
} from '../components';
import type { ScalableProductCARD_STYLE_TYPE, ScalableStyle } from '../scalableStyles';
import { defaultScalableStyle } from '../scalableStyles/presets';
import { ScalableProductCardList } from './list';
import { ScalableProductCardMobile } from './mobile';
import { ScalableProductContainer } from './styled';
import { ScalableProductCardTile } from './tile';

export enum SCALABLE_PRODUCT_CARD_TYPE {
    TILE = 'tile',
    LIST = 'list',
    MOBILE = 'mobile'
}

export type DefaultScalableProductCardProps = ScalableStyle<ScalableProductCARD_STYLE_TYPE> & {
    topContent?: ReactNode;
    bottomContent?: ReactNode;
    onQuantityChange?: (quantity: number) => void;
    onPositionTextChange?: (positionText?: string) => void;
    mainId?: string;
    isVisible?: boolean;
    product: {
        lineItemId?: string;
        defaultQuantity: number;
        defaultPositionText?: string;
        information: InformationContainerType;
        isRewardProduct?: boolean;
        sku: string;
        tags?: TagsContainerProps;
        image?: ImageContainerProps;
        icons?: IconsContainerProps;
        ids?: IdsContainerProps;
        name?: NameContainerProps;
        additionalLinks?: AdditionalLinksContainerProps;
        comparison?: Pick<ComparableProductType, 'sku' | 'title' | 'imageSrc'>;
        prices: Pick<PricesType, 'quantityUnit' | 'salesUnit' | 'hidePrice'>;
        availability: Pick<AvailabilitiesProps, 'quantityUnit' | 'availability' | 'customDeliveryInfo'>;
        externalProductPrice?: PositionPriceObject;
        order: Pick<
            OrderContainerProps,
            | 'quantityUnit'
            | 'salesUnit'
            | 'sku'
            | 'isUnqualifiedContractProduct'
            | 'chargeNr'
            | 'position'
            | 'packagingSize'
            | 'bestPrice'
            | 'projectNumber'
            | 'cartId'
            | 'maxQuantity'
            | 'hidePrice'
            | 'isPackageItem'
            | 'cableSnaps'
            | 'supplierId'
            | 'stockQuantity'
            | 'successorProduct'
            | 'ean'
            | 'nocut'
        >;
    };
};

export type ScalableProductCardProps = React.ComponentPropsWithoutRef<'div'> &
    DefaultScalableProductCardProps & {
        cardType?: SCALABLE_PRODUCT_CARD_TYPE;
    };

export const ScalableProductCard: FC<ScalableProductCardProps> = (props) => {
    const {
        cardType = SCALABLE_PRODUCT_CARD_TYPE.TILE,
        className,
        product,
        scalableStyle = defaultScalableStyle,
        topContent,
        bottomContent,
        onQuantityChange,
        onPositionTextChange,
        mainId,
        ...rest
    } = props;

    const scalableProductProps: DefaultScalableProductCardProps = {
        product,
        scalableStyle,
        topContent,
        bottomContent,
        mainId,
        onQuantityChange,
        onPositionTextChange
    };

    const scalableProductCardClasses = getClassNames({
        className,
        prefix: 'scalable-product-card',
        modifier: { 'card-type': cardType }
    });

    return (
        <ScalableProductContainer {...rest} className={scalableProductCardClasses}>
            {cardType === SCALABLE_PRODUCT_CARD_TYPE.TILE && <ScalableProductCardTile {...scalableProductProps} />}
            {cardType === SCALABLE_PRODUCT_CARD_TYPE.LIST && <ScalableProductCardList {...scalableProductProps} />}
            {cardType === SCALABLE_PRODUCT_CARD_TYPE.MOBILE && <ScalableProductCardMobile {...scalableProductProps} />}
        </ScalableProductContainer>
    );
};
